import { useToggleButton } from "@react-aria/button";
import { mergeProps } from "@react-aria/utils";
import { useToggleState } from "@react-stately/toggle";
import { useRef } from "react";
import { Button, ButtonProps } from "../button/button";
import { Tooltip } from "../tooltip/tooltip";

export type ToggleButtonProps = Omit<ButtonProps, "aria-label"> & {
  ariaLabelOff: string;
  ariaLabelOn: string;
  defaultSelected?: boolean;
  disableTooltip?: boolean;
  isSelected?: boolean;
  onChange?: (isSelected: boolean) => void;
  ref?: React.Ref<HTMLButtonElement>;
};

export function ToggleButton({
  disableTooltip,
  ref,
  ...props
}: ToggleButtonProps) {
  const { children, defaultSelected, isSelected, onChange, ...otherProps } =
    props;

  const state = useToggleState({ defaultSelected, isSelected, onChange });

  const innerRef = useRef<HTMLButtonElement>(null);

  const { buttonProps } = useToggleButton(props, state, innerRef);

  const buttonElement = (
    <Button
      {...mergeProps(otherProps, buttonProps)}
      ref={ref}
      aria-label={state.isSelected ? props.ariaLabelOn : props.ariaLabelOff}
      aria-pressed={state.isSelected}
      data-selected={state.isSelected}
      onPress={() => {
        // @ts-expect-error - buttonProps.onClick is not typed
        buttonProps.onClick?.();
      }}
    >
      {children}
    </Button>
  );

  if (props.isIconOnly && !disableTooltip) {
    return (
      <Tooltip
        content={state.isSelected ? props.ariaLabelOn : props.ariaLabelOff}
      >
        {buttonElement}
      </Tooltip>
    );
  }

  return buttonElement;
}

ToggleButton.displayName = "RecareUI.ToggleButton";
